import { elevate, localStorageBackedSession, ProductGroup, Product, ContentItem } from '@apptus/esales-api';
import handleFromUrl from '../../../../../util/handleFromUrl';
import { getClusterId, getLocale, getMarket, getTouchpoint, getViewId, getVoyadoSession, hasCookieConsent } from './voyadoHelpers';
import { elevateFetchShim } from './elevateFetchShim';
import { PageMeta } from '../types';

/*
  From Voyado docs
  > The ticket is a unique string for an object generated by Elevate.
  In order to track notifications (product clicked, product added to cart) we need these tickets

  This file generates tickets and stores them in a localStorage item.
  It does this in the following instances:
  - When a search is performed and the product is mapped (./mappers/mapPredSearchProduct.ts)
  - When a PLP/Search product is mapped (./mappers/mapProduct.ts)
  - When a PDP is loaded ('/scripts/sections/product.ts)




  Depending on which page we land on we need to generate new tickets.
  We can access data from ShopifyAnalytics.meta.page window object
  ShopifyAnalytics.meta.page.pageType
  ShopifyAnalytics.meta.page.resourceType
  ShopifyAnalytics.meta.page.resourceId

  don't have resourceId

  home
  searchresults

  do have resource ID

  collection
  product
  page
  article
  blog

  if a resourceId exists, use that
  if searchResults use q query param
  if home, use home

  if recommendation, handle separately?
*/

interface VoyadoResponse {
  productGroup: ProductGroup;
}

interface TicketsStorage {
  [customerKey: string]: {
    [sessionKey: string]: {
      [resourceId: string]: {
        [handle: string]: string;
      };
    };
  };
}

// Initialize API with session based on cookie consent
// Use ephemeral session (random per request) if cookies declined, otherwise persisted session
const getApi = () => {
  const session = getVoyadoSession(localStorageBackedSession);
  return elevate({
    clusterId: getClusterId(),
    market: getMarket(),
    locale: getLocale(),
    touchpoint: getTouchpoint(),
    session: session
  });
};

// Set the storage key
const STORAGE_KEY = 'bb-voyado-tickets';

// Get the localStorage item
const getStoredTickets = (): TicketsStorage => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading tickets from localStorage:', error);
    return {};
  }
};

// Set the localStorage item
const setStoredTickets = (tickets: TicketsStorage): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tickets));
  } catch (error) {
    console.error('Error writing tickets to localStorage:', error);
  }
};

// Generate the ticket from the product
const generateTicket = (product: Product, pageMeta: PageMeta, isFresh = false): void => {
  // Don't store tickets in localStorage if cookies are declined
  if (!hasCookieConsent()) {
    return;
  }

  // ONLY store tickets that are explicitly marked as "fresh" (i.e coming from a fresh API call)
  // This prevents tickets from cached/stale HTML or JSON from being "adopted" into the user's current session Key.
  // This has been changed off the back of Voyado feedback - https://blubolt.teamwork.com/app/tasks/41471679
  if (!isFresh) {
    return;
  }


  if (!product?.link || !product?.ticket) return;

  // Get current session keys to scope tickets per user and session
  const session = getVoyadoSession(localStorageBackedSession);
  const sessionKeys = session();
  const customerKey = sessionKeys.customerKey;
  const sessionKey = sessionKeys.sessionKey;

  if (!customerKey || !sessionKey) {
    console.warn('Cannot store ticket: missing customerKey or sessionKey');
    return;
  }

  // Format resourceId based on page type
  // Ensure this and getCurrentResourceId within notifications.ts remain aligned if either change
  let resourceId: string;

  if (!pageMeta || !pageMeta.pageType) {
    console.warn('generateTicket: missing pageMeta or pageType');
    return;
  }

  if (pageMeta.pageType === 'home') {
    resourceId = 'home';
  } else if (pageMeta.pageType === 'searchresults') {
    resourceId = `searchresults-${new URLSearchParams(window.location.search).get('q')}`;
  } else if (pageMeta.pageType === 'recommendations') {
    resourceId = pageMeta.resourceId?.toString() || 'undefined';
  } else {
    // For collection, product, page, article, blog - prepend the pageType
    resourceId = `${pageMeta.pageType}-${pageMeta.resourceId?.toString() || 'undefined'}`;
  }

  // Get the product handle
  const key = handleFromUrl(product.link);

  // Get the localStorage item
  const storedTickets = getStoredTickets();
  // Initialize the nested structure if it doesn't exist
  if (!storedTickets[customerKey]) {
    storedTickets[customerKey] = {};
  }
  if (!storedTickets[customerKey][sessionKey]) {
    storedTickets[customerKey][sessionKey] = {};
  }
  if (!storedTickets[customerKey][sessionKey][resourceId]) {
    storedTickets[customerKey][sessionKey][resourceId] = {};
  }

  // If the product already exists under this resourceId, do nothing
  if (storedTickets[customerKey][sessionKey][resourceId][key]) return;

  // Otherwise, add the ticket to the object under the resourceId
  storedTickets[customerKey][sessionKey][resourceId][key] = product.ticket;
  setStoredTickets(storedTickets);
};

// Generate ticket for content items (articles, pages, collections)
export const generateContentTicket = (contentItem: ContentItem, searchTerm: string): void => {
  // Don't store tickets in localStorage if cookies are declined
  if (!hasCookieConsent()) {
    return;
  }

  if (!contentItem?.link || !contentItem?.ticket) {
    console.log('generateContentTicket: missing link or ticket', { link: contentItem?.link, ticket: contentItem?.ticket });
    return;
  }

  // Get current session keys to scope tickets per user and session
  const session = getVoyadoSession(localStorageBackedSession);
  const sessionKeys = session();
  const customerKey = sessionKeys.customerKey;
  const sessionKey = sessionKeys.sessionKey;

  if (!customerKey || !sessionKey) {
    console.warn('Cannot store content ticket: missing customerKey or sessionKey');
    return;
  }

  // Use autocomplete-specific resource ID for content items
  const resourceId = `autocomplete-${searchTerm}`;
  const key = handleFromUrl(contentItem.link);

  console.log('generateContentTicket: storing ticket', { customerKey, sessionKey, resourceId, key, ticket: contentItem.ticket });

  // Get the localStorage item
  const storedTickets = getStoredTickets();

  // Initialize the nested structure if it doesn't exist
  if (!storedTickets[customerKey]) {
    storedTickets[customerKey] = {};
  }
  if (!storedTickets[customerKey][sessionKey]) {
    storedTickets[customerKey][sessionKey] = {};
  }
  if (!storedTickets[customerKey][sessionKey][resourceId]) {
    storedTickets[customerKey][sessionKey][resourceId] = {};
  }

  // If the content item already exists under this resourceId, do nothing
  if (storedTickets[customerKey][sessionKey][resourceId][key]) return;

  // Otherwise, add the ticket to the object under the resourceId
  storedTickets[customerKey][sessionKey][resourceId][key] = contentItem.ticket;
  setStoredTickets(storedTickets);

  console.log('generateContentTicket: ticket stored successfully');
};

// Get the ticket from localStorage
export const getTicket = (identifer: string, resourceId: string): string => {
  // Get current session keys to retrieve tickets for the current user and session
  const session = getVoyadoSession(localStorageBackedSession);
  const sessionKeys = session();
  const customerKey = sessionKeys.customerKey;
  const sessionKey = sessionKeys.sessionKey;

  if (!customerKey || !sessionKey) {
    return '';
  }

  const storedTickets = getStoredTickets();
  return storedTickets[customerKey]?.[sessionKey]?.[resourceId]?.[identifer] || '';
};

// Get the product from Voyado
export const setTicket = async (key: string): Promise<void> => {
  // Don't fetch or store tickets if cookies are declined
  if (!hasCookieConsent()) {
    return;
  }

  elevateFetchShim();

  try {
    const api = getApi();
    const result = await api.query.productPage(
      {
        viewId: getViewId(),
        productKey: key
      },
      { productGroup: { include: true } }
    ) as VoyadoResponse;

    // Voyado returns an array even when requesting a single product
    // We need the first product in the array
    const product = result?.productGroup?.products?.[0];
    const pageMeta = (window as any).ShopifyAnalytics?.meta?.page;

    if (product && pageMeta) {
      generateTicket(product, pageMeta);
    } else if (product) {
      console.warn('setTicket: product found but ShopifyAnalytics.meta.page is missing');
    }
  } catch (error) {
    console.error('Error fetching product ticket:', error);
  }
};

export default generateTicket;
